/* eslint-disable @typescript-eslint/no-unused-vars */
import { authorizedApiClient } from "../config";
import notify from "../../utils/notify";
import type {
  AIMemoryObjectType,
  EnableAIMemoryObjectParams,
  EnableAIMemoryObjectResponse,
  GetFieldDefinitionsParams,
  GetFieldDefinitionsResponse,
  ListAIMemoryObjectsParams,
  ListAIMemoryObjectsResponse,
  UpdateFieldDefinitionParams,
  UpdateFieldDefinitionResponse,
} from "./aiMemoryEndpoints.interface";
import type { GetWorkspaceObjectsResponse } from "../syncConfiguration/syncConfigurationEndpoints.interface";

const showErrorAlert = (err: any) => notify({
  heading: "Unknown Error",
  type: "failure",
  messageList: [
    "Kindly contact Hyperengage support to help you resolve this issue.",
  ],
});

// List objects and map to AI Memory status shape (uses existing workspace objects endpoint)
export const listAIMemoryObjects = async (
  { workspace_id }: ListAIMemoryObjectsParams,
): Promise<ListAIMemoryObjectsResponse> => {
  try {
    const response = await authorizedApiClient.get<GetWorkspaceObjectsResponse>(
      `/workspace/${workspace_id}/objects`,
    );
    const objects = response.data || [];
    const data = objects.map((o: any) => ({
      api_slug: o.api_slug as AIMemoryObjectType,
      status: o.enabled ? ('enabled' as const) : ('disabled' as const),
      field_count: o.field_count,
      object_id: o.id, // Add the object ID from the workspace objects response
    }));
    return { data };
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

// Enable an AI Memory object for a workspace
export const enableAIMemoryObject = async (
  { workspace_id, object_type }: EnableAIMemoryObjectParams,
): Promise<EnableAIMemoryObjectResponse> => {
  try {
    const response = await authorizedApiClient.post<EnableAIMemoryObjectResponse>(
      `/workspace/${workspace_id}/objects/${object_type}/enable`,
      {},
    );
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};
// Note: Field definitions fetch endpoint is not specified under ai-memory.
// Keeping the function for compatibility would require a supported endpoint.
// Currently unused by the AI Memory page.
export const getAIMemoryFieldDefinitions = async (
  { workspace_id, object_id }: GetFieldDefinitionsParams,
): Promise<GetFieldDefinitionsResponse> => {
  try {
    // Use the existing fields endpoint with object ID
    const response = await authorizedApiClient.get<{ data: any[] }>(
      `/workspace/${workspace_id}/objects/${object_id}/fields`,
      { params: { include_disabled: true } }
    );

    const raw: any = response?.data ?? [];
    // Map to AI Memory FieldDefinition shape, preserving is_searchable if available
    const data = raw
      .filter((f: any) => (f?.type ? f.type === 'field' : true))
      .map((f: any) => ({
        id: f.id,
        column_name: f.column_name || f.name,
        display_name: f.display_name || f.name,
        is_searchable: typeof f.is_searchable === 'boolean' ? f.is_searchable : false,
        is_system_field: !!f.is_system_field,
        description: f.description,
        object_id,
      }));

    return { data };
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

// No workspace-level AI Memory status endpoint in current backend

// Update a field definition (e.g., toggle is_searchable)
export const updateAIMemoryFieldDefinition = async (
  { workspace_id, field_id, data }: UpdateFieldDefinitionParams,
): Promise<UpdateFieldDefinitionResponse> => {
  try {
    const response = await authorizedApiClient.put<UpdateFieldDefinitionResponse>(
      `/workspace/${workspace_id}/fields/${field_id}`,
      data,
    );
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};
